import { useCargaRutas, type CargaRuta } from '../../hooks/useCargaRutas'
import { colorDeDia } from './coloresDia'

/**
 * Panel de carga por ruta (R7 · B.7).
 *
 * Muestra, de un vistazo, cuántos clientes hay agendados cada día de la semana
 * (r1..r5), con el color de cada ruta y una barra proporcional. Es la lectura
 * que la spec pide "visible desde la sección del panel (admin)": con ella se ve
 * qué día va cargado y cuál tiene hueco, que es lo que decide la ruta de menor
 * carga que se le ofrece a cada cliente.
 */
export function CargaRutas() {
  const { data, isPending, isError } = useCargaRutas()
  const rutas = data ?? []
  const maximo = Math.max(1, ...rutas.map((r) => r.carga))
  const total = rutas.reduce((suma, r) => suma + r.carga, 0)

  if (isError) return null // el panel principal ya avisa; esto es complementario

  return (
    <section
      aria-label="Carga por ruta de la semana"
      className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie p-4"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-tinta">Carga por ruta</h2>
        <span className="text-xs text-tinta-apagada">
          {isPending ? 'Cargando…' : `${total} repartos esta semana`}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {(isPending ? PLACEHOLDER : rutas).map((r) => (
          <BarraRuta key={r.ruta} ruta={r} maximo={maximo} cargando={isPending} />
        ))}
      </div>
    </section>
  )
}

const PLACEHOLDER: CargaRuta[] = [
  { ruta: 'r1', dia: 'lunes', carga: 0 },
  { ruta: 'r2', dia: 'martes', carga: 0 },
  { ruta: 'r3', dia: 'miércoles', carga: 0 },
  { ruta: 'r4', dia: 'jueves', carga: 0 },
  { ruta: 'r5', dia: 'viernes', carga: 0 },
]

function BarraRuta({ ruta, maximo, cargando }: { ruta: CargaRuta; maximo: number; cargando: boolean }) {
  const pct = Math.round((ruta.carga / maximo) * 100)
  const color = colorDeDia(ruta.ruta)
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs font-medium capitalize text-tinta-2">{ruta.dia}</span>
      <div className="relative h-5 flex-1 overflow-hidden rounded-md bg-elevado">
        {!cargando ? (
          <div
            className={`h-full rounded-md ${color.fondo} transition-all`}
            style={{ width: `${Math.max(pct, ruta.carga > 0 ? 6 : 0)}%` }}
          />
        ) : null}
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums text-tinta">
        {cargando ? '·' : ruta.carga}
      </span>
    </div>
  )
}
